import { CARGO_DIRECTOR, CONTACT_ROL, CONTACT_TYPE, IncorporationContactSchema, SI_NO_OPTIONS } from "@/schemas/incorporation";
import z from "zod";
import { isDirectorContact, isShareholderContact } from "./contact-helper";

export const contactFormSchema = IncorporationContactSchema.extend({
  contact_type: z.enum(CONTACT_TYPE, {
    message: "directors.contact_form.contact_type.required",
  }),
  rol: z.array(z.enum(CONTACT_ROL)).min(1, { message: "options_required" }),
  nombre: z
    .string()
    .min(1, { message: "directors.contact_form.nombre.required" }),
  email: z.string().email({ message: "directors.contact_form.email.required" }),
  telefono: z
    .string()
    .min(1, { message: "directors.contact_form.telefono.required" }),
  direccion: z
    .string()
    .min(1, { message: "directors.contact_form.direccion.required" }),
  nacionalidades: z
    .array(z.string())
    .min(1, { message: "directors.contact_form.nacionalidades.required" }),
  nacionalidad_principal: z.string().optional(),
  residencia_fiscal: z
    .string().optional(),
  cargo: z.enum(CARGO_DIRECTOR).optional(),
  n_certificados: z.string().optional(),
}).transform(data => {
  if (data.es_beneficiario === SI_NO_OPTIONS[1]) {
    return {
      ...data,
      porcentaje_beneficio: 0,
    }
  }
  return data;
})
.superRefine((data, ctx) => {
  const hasAccionista = isShareholderContact(data); // "accionista"
  const hasDirector = isDirectorContact(data); // "director"

  // Only require porcentaje_beneficio and n_certificados if accionista role is selected
  if (hasAccionista) {
    if (!data.porcentaje_beneficio) {
      ctx.addIssue({
        code: 'custom',
        path: ["porcentaje_beneficio"],
        message: "directors.contact_form.porcentaje_beneficio.required",
      });
    } else {
      // Validate the format and range if provided
      const num = data.porcentaje_beneficio || 0;
      if (isNaN(num) || num < 0.01 || num > 100) {
        ctx.addIssue({
          code: 'custom',
          path: ["porcentaje_beneficio"],
          message: "directors.contact_form.porcentaje_beneficio.invalid_range",
        });
      }
    }
  }
  // Require cargo only when the director role is selected
  if (hasDirector) {
    const isShareholder = data.es_beneficiario === SI_NO_OPTIONS[0]; // "si"
    // Require porcentaje_beneficio when shareholder is selected
    if (isShareholder) {
      if (!data.porcentaje_beneficio) {
        ctx.addIssue({
          code: 'custom',
          path: ["porcentaje_beneficio"],
          message: "directors.contact_form.porcentaje_beneficio.required",
        });
      } else {
        // Validate the format and range if provided
        const num = data.porcentaje_beneficio || 0;
        if (isNaN(num) || num < 0.01 || num > 100) {
          ctx.addIssue({
            code: 'custom',
            path: ["porcentaje_beneficio"],
            message: "directors.contact_form.porcentaje_beneficio.invalid_range",
          });
        }
      }
    }
    if (!data.es_beneficiario) {
      ctx.addIssue({
        code: 'custom',
        path: ["es_beneficiario"],
        message: "directors.contact_form.es_beneficiario.required",
      });
    }
    if (!data.cargo || data.cargo.trim().length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ["cargo"],
        message: "directors.contact_form.cargo.required",
      });
    }
    if (!data.residencia_fiscal) {
      ctx.addIssue({
        code: 'custom',
        path: ["residencia_fiscal"],
        message: "directors.contact_form.residencia_fiscal.required",
      });
    }
  }

  // If there are nacionalidades selected, nacionalidad_principal is required
  // and must be one of the selected nacionalidades
  const selectedNationalities = data.nacionalidades || [];
  if (selectedNationalities.length > 0) {
    if (
      !data.nacionalidad_principal ||
      !selectedNationalities.includes(data.nacionalidad_principal)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ["nacionalidad_principal"],
        message: "directors.contact_form.nacionalidad_principal.required",
      });
    }
  }
});

export type ContactFormValues = z.infer<typeof contactFormSchema>;